/*
 * Copyright (c) Airbus SAS. All rights reserved.
 */

/**
 * Keep <turbo-frame data-frame-poll> frames converged on the server's state by
 * reloading them from the DB once Mercure goes QUIET — a watchdog, not a
 * metronome, so it reconciles a missed update without duplicating the live ones.
 *
 * Why "stream silence" and not "busy" or "disconnected": the stuck-spinner bug
 * is a publish-before-render race, NOT a dropped connection. A job can finish in
 * Messenger before its POST response even renders, so the worker broadcasts the
 * terminal `replace` over Mercure while the row it targets does not exist in the
 * DOM yet — Turbo drops it (no matching target) and no further event ever comes.
 * The EventSource is fully connected the whole time, so a connection check can't
 * see the gap. What we CAN observe is that no Turbo Stream has rendered into the
 * frame for a while; that silence, with the frame still busy, is the cue to
 * reload and reconcile from the DB.
 *
 * Mechanism: arm a per-frame timer when the frame turns busy; every Turbo Stream
 * that renders into the frame (a live Mercure update) pushes the timer back.
 * While updates keep arriving the timer never fires (no redundant reloads); once
 * they stop for `interval` ms while still busy, reload once, then wait again.
 *
 * Registered ONCE at boot (a module), NOT per-element: what arms/disarms the
 * watchdog is the frame's BUSY state (busy rows are added/removed by Turbo
 * Streams at any time), not the frame's presence in a mounted container. A
 * document-level MutationObserver arms/disarms the watchdog as frames turn
 * busy or leave the DOM; a document-level `turbo:before-stream-render`
 * listener feeds it Mercure's heartbeat.
 *
 * Opt in with `data-frame-poll` on a turbo-frame that has a `src` (so reload()
 * works); pair with `refresh="morph"` to morph rows in place and leave any
 * `data-turbo-permanent` child (the live <turbo-mercure-stream-source>) untouched:
 *
 *   data-frame-poll-while=".spinner-border"  selector meaning "still busy" (default)
 *   data-frame-poll-interval="2500"          ms of Mercure silence before a reload (default)
 *   data-frame-poll-max="20"                 consecutive fallback reloads before giving up (default)
 *
 * @type {Module}
 */
export default function init() {
    /** @type {Map<Element, { timer: number, reloads: number, interval: number, max: number }>} */
    const polls = new Map()

    const isBusy = (frame) => frame.querySelector(frame.dataset.framePollWhile || '.spinner-border') !== null

    const stop = (frame) => {
        clearTimeout(polls.get(frame)?.timer)
        polls.delete(frame)
    }

    // (Re)arm the watchdog. If nothing freshens the frame within `interval`, a
    // Mercure update was missed (or is never coming) — reconcile from the DB, then
    // wait for the next silence. Each silent reload spends one of `max` attempts.
    const arm = (frame) => {
        const state = polls.get(frame)
        clearTimeout(state.timer)
        state.timer = setTimeout(() => {
            if (!frame.isConnected || state.reloads++ >= state.max || !isBusy(frame)) {
                stop(frame)
                return
            }
            frame.reload()
            arm(frame)
        }, state.interval)
    }

    const ensurePolling = (frame) => {
        if (polls.has(frame)) {
            return
        }
        polls.set(frame, {
            timer: 0,
            reloads: 0,
            interval: Number.parseInt(frame.dataset.framePollInterval, 10) || 2500,
            max: Number.parseInt(frame.dataset.framePollMax, 10) || 20,
        })
        arm(frame)
    }

    // A Turbo Stream rendered into the frame — Mercure is alive and pushing, so
    // reset the fallback budget and push the watchdog back: the next reload only
    // fires if this update is NOT followed by another within `interval`.
    const deferToMercure = (frame) => {
        polls.get(frame).reloads = 0
        arm(frame)
    }

    // Arm a frame the moment it turns busy; drop it when it goes idle or leaves
    // the DOM. One batched scan per mutation batch — cheap, and a no-op whenever
    // no poll-frame is on the page.
    const sync = () => {
        for (const frame of document.querySelectorAll('turbo-frame[data-frame-poll]')) {
            if (isBusy(frame)) {
                ensurePolling(frame)
            } else if (polls.has(frame)) {
                stop(frame)
            }
        }
        for (const frame of polls.keys()) {
            if (!frame.isConnected || !frame.hasAttribute('data-frame-poll')) {
                stop(frame)
            }
        }
    }

    // Mercure's heartbeat: every pushed Turbo Stream fires this before it patches
    // the DOM. Map the stream's target back to its poll-frame and defer that poll.
    // Our own frame.reload() is a FRAME render, not a STREAM render, so it never
    // trips this — the poll can't reset itself.
    document.addEventListener('turbo:before-stream-render', (e) => {
        const targetId = e.detail.newStream?.getAttribute('target')
        const frame = targetId && document.getElementById(targetId)?.closest('turbo-frame[data-frame-poll]')
        if (frame && polls.has(frame)) {
            deferToMercure(frame)
        }
    })

    // Observe <html>, not <body>: a Turbo Drive visit swaps in a NEW <body>, which an observer bound to the
    // boot-time one never sees, so the watchdog would stay dead after the first navigation.
    new MutationObserver(sync).observe(document.documentElement, { childList: true, subtree: true })
    sync()
}
